/**
 * To'lov provayderlari ro'yxati.
 *
 * Ikki xil provayder turi bor:
 *
 *  1. `kind: "redirect"` (default) — Click / Payme / Paynet.
 *     `buildUrl` tashqi checkout URL ini quradi, foydalanuvchi o'sha sahifaga
 *     olib chiqiladi (`openExternalPaymentLink`).
 *
 *  2. `kind: "inApp"` — Zoomrad.
 *     Hech qayerga o'tilmaydi: foydalanuvchi mini app ichida o'z kartasini
 *     tanlaydi va to'lov API orqali shu yerda bajariladi
 *     (`payZoomradFee` — `@/api/zoomrad.api`).
 *
 * Yangi provayder qo'shish uchun shu massivga yangi element qo'shing:
 *  - available: false  → "Tez kunda" badge bilan disabled holat
 *  - available: true   → to'liq ishlaydi
 *
 * Eslatma: `available` — provayderning O'Z hostidagi holati. Umumiy (bot)
 * selektorida vaqtincha "Tez orada" qilib ko'rsatish uchun `available` emas,
 * `PaymentMethods` ichidagi `COMING_SOON_IN_SELECTOR` ishlatiladi.
 */

import { getMiniAppOrigin } from "@/lib/payment";

/** Qo'llab-quvvatlanadigan to'lov provayderlari */
export type ProviderId = "click" | "payme" | "uzum" | "zoomrad" | "paynet";

/** To'lov qanday bajariladi: tashqi sahifaga redirect yoki mini app ichida */
export type ProviderKind = "redirect" | "inApp";

interface BasePaymentProvider {
  id: ProviderId;
  label: string;
  /** Tailwind gradient class (bg + text) */
  gradient: string;
  /** Agar false bo'lsa — disabled + "Tez kunda" badge */
  available: boolean;
}

/** Tashqi checkout sahifasiga olib chiqadigan provayder */
export interface RedirectPaymentProvider extends BasePaymentProvider {
  kind?: "redirect";
  buildUrl: (child: any, amount: number) => string;
}

/** Mini app ichida (API orqali) to'lovni bajaradigan provayder */
export interface InAppPaymentProvider extends BasePaymentProvider {
  kind: "inApp";
}

export type PaymentProvider = RedirectPaymentProvider | InAppPaymentProvider;

/**
 * Type guard — provayder mini app ichida to'laydimi (redirect yo'q).
 * Narrowing tufayli `buildUrl` ni faqat redirect provayderlarda chaqirish
 * mumkin bo'ladi (TS compile vaqtida ushlaydi).
 */
export function isInAppProvider(
  provider: PaymentProvider,
): provider is InAppPaymentProvider {
  return provider.kind === "inApp";
}

export const PAYMENT_PROVIDERS: PaymentProvider[] = [
  {
    id: "click",
    label: "Click",
    gradient: "from-blue-500 to-cyan-500",
    available: true,
    buildUrl: (child, amount) => {
      // Qaytish manzili joriy host domenidan quriladi — Click ichida
      // click.insonuchun.uz, Paynet ichida paynet.insonuchun.uz va h.k.
      // Click `return_url` ni bir marta dekod qiladi, ichkaridagi `url` ni esa
      // yana bir marta — shuning uchun ikki qavat encode.
      const miniApp = `${getMiniAppOrigin()}/?patientGo=${child.id}`;
      const webView = `https://my.click.uz/app/webView?auth=true&hide_cross=true&show_header=true&url=${encodeURIComponent(miniApp)}`;
      return `https://my.click.uz/services/pay/?service_id=${child.serviceId}&merchant_id=${child.merchantId}&amount=${amount}&transaction_param=${child.externalId}&merchant_user_id=${child.merchantUserId}&return_url=${encodeURIComponent(webView)}`;
    },
  },
  {
    id: "payme",
    label: "Payme",
    gradient: "from-blue-700 to-blue-500",
    available: true,
    buildUrl: (child, amount) => {
      let payload = "";
      const PAYME_RETURN_URL = `${getMiniAppOrigin()}/?patientGo=${child.id}`;

      if (child.fondId == 14) {
        //ezgu amal
        payload = `m=${child.paymeeMerchantId};ac.order_id=${child.externalId};a=${amount * 100};c=${PAYME_RETURN_URL};`;
        return `https://checkout.paycom.uz/${btoa(payload)}`;
      } else if (child.fondId == 13) {
        //ixlos nuri
        payload = `m=${child.paymeeMerchantId};ac.id=${child.externalId};a=${amount * 100};c=${PAYME_RETURN_URL};`;
        return `https://checkout.paycom.uz/${btoa(payload)}`;
      } else if (child.fondId == 5) {
        // ona
        payload = `m=${child.paymeeMerchantId};ac.account_id=${child.externalId};a=${amount * 100};c=${PAYME_RETURN_URL};`;
        return `https://checkout.paycom.uz/${btoa(payload)}`;
      }
      payload = `m=${child.paymeeMerchantId};ac.user_data=${child.externalId};a=${amount * 100};c=${PAYME_RETURN_URL};`;
      return `https://checkout.paycom.uz/${btoa(payload)}`;
      // const payload = `m=${child.paymeeMerchantId};ac.order_id=${child.externalId};a=${amount * 100};ac.account_id=${child?.externalId ?? ""};ac.id=${child?.externalId ?? ""};ac.user_data=${child.externalId ?? ""}`;
    },
  },
  {
    id: "paynet",
    label: "Paynet",
    gradient: "from-teal-600 to-emerald-500",
    available: true,
    buildUrl: (child, amount) => {
      return `https://app.paynet.uz/?m=${child.paynetMerchantId}&fa=${amount}&i=${child.externalId}`;
    },
  },
  {
    // Zoomrad — yagona in-app provayder: karta tanlanadi va to'lov
    // `ZoomradPayFee` orqali mini app ichida bajariladi (`buildUrl` yo'q).
    id: "zoomrad",
    label: "Zoomrad",
    gradient: "from-emerald-500 to-teal-500",
    available: true,
    kind: "inApp",
  },
];

/** Default (birinchi mavjud) provaydern */
export const DEFAULT_PROVIDER_ID =
  PAYMENT_PROVIDERS.find((p) => p.available)?.id ?? PAYMENT_PROVIDERS[0].id;
